import { useEffect, useState } from 'react';
import { Calendar, Loader2 } from 'lucide-react';
import { format, isAfter, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { StatCard } from '@/components/common/StatCard';
import { getPatientByUserId, getAppointmentsByPatient } from '@/services/index';
import type { AppointmentDTO } from '@/services/appointments';

interface PatientNextAppointmentCardProps {
  userId: string;
}

export function PatientNextAppointmentCard({ userId }: PatientNextAppointmentCardProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [nextAppointment, setNextAppointment] = useState<AppointmentDTO | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNextAppointment = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Obter o perfil do paciente pelo ID do usuário
        const patientResponse = await getPatientByUserId(userId);
        
        if (patientResponse.success && patientResponse.data) {
          const patientId = patientResponse.data.id;
          
          // 2. Obter as consultas do paciente
          const appointmentsResponse = await getAppointmentsByPatient(patientId);
          
          if (appointmentsResponse.success && appointmentsResponse.data) {
            const now = new Date();
            
            // 3. Filtrar consultas futuras e válidas
            const futureAppointments = appointmentsResponse.data
              .filter(app => {
                // Status que indicam uma consulta futura ativa
                const validStatuses = [
                  'scheduled', 
                  'requested', 
                  'CONFIRMADO', 
                  'RESERVADO_PENDENTE', 
                  'reschedule_requested',
                  'in_transit'
                ];
                
                const isStatusValid = validStatuses.includes(app.status);
                
                // Priorizar appointmentDate (ISO string completa) para comparação precisa
                let appFullDate: Date;
                if (app.appointmentDate) {
                  appFullDate = parseISO(app.appointmentDate);
                } else if (app.date && app.time && app.time !== 'undefined:undefined') {
                  // Fallback para composição de date e time se appointmentDate não existir
                  // Garantir que compomos como algo que o parseISO entenda como local ou UTC conforme necessário
                  try {
                    appFullDate = parseISO(`${app.date}T${app.time.padStart(5, '0')}:00`);
                  } catch (e) {
                    return false;
                  }
                } else if (app.date) {
                  try {
                    appFullDate = parseISO(`${app.date}T00:00:00`);
                  } catch (e) {
                    return false;
                  }
                } else {
                  return false;
                }
                
                return isStatusValid && isValid(appFullDate) && isAfter(appFullDate, now);
              })
              // Ordenar pela data mais próxima
              .sort((a, b) => {
                const getDate = (app: AppointmentDTO) => {
                  if (app.appointmentDate) return parseISO(app.appointmentDate);
                  if (app.date && app.time && app.time !== 'undefined:undefined') return parseISO(`${app.date}T${app.time.padStart(5, '0')}:00`);
                  if (app.date) return parseISO(`${app.date}T00:00:00`);
                  return new Date(8640000000000000); // Far future
                };
                return getDate(a).getTime() - getDate(b).getTime();
              });

            if (futureAppointments.length > 0) {
              setNextAppointment(futureAppointments[0]);
            } else {
              setNextAppointment(null);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching next appointment:', err);
        setError('Não foi possível carregar a próxima consulta');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchNextAppointment();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-900 rounded-xl p-6 border border-gray-100 dark:border-neutral-800 flex items-center justify-center min-h-[140px]">
        <Loader2 className="w-6 h-6 text-teal-600 animate-spin mr-2" />
        <span className="text-sm text-gray-500">Buscando próxima consulta...</span>
      </div>
    );
  }

  // Formatação da data por extenso
  const getFormattedDate = (appointment: AppointmentDTO) => {
    try {
      let date: Date;
      if (appointment.appointmentDate) {
        date = parseISO(appointment.appointmentDate);
      } else if (appointment.date && appointment.time) {
        date = parseISO(`${appointment.date}T${appointment.time}:00`);
      } else {
        return 'Data indisponível';
      }

      if (!isValid(date)) return 'Data inválida';
        
      // EEEE = dia da semana, dd = dia, MMMM = mês por extenso
      const formatted = format(date, "EEEE, dd 'de' MMMM 'às' HH:mm", { locale: ptBR });
      
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch (e) {
      return 'Data inválida';
    }
  };

  const displayValue = nextAppointment 
    ? getFormattedDate(nextAppointment)
    : "Nenhuma consulta agendada";

  const getStatusDescription = (status: string) => {
    switch(status) {
      case 'requested':
      case 'RESERVADO_PENDENTE':
        return '(Aguardando confirmação)';
      case 'reschedule_requested':
        return '(Reagendamento solicitado)';
      case 'in_transit':
        return '(Médico a caminho)';
      default:
        return '';
    }
  };

  const getTypeLabel = (type?: string) => {
    switch(type) {
      case 'video': return 'Telemedicina';
      case 'home': return 'Domiciliar';
      case 'clinic': return 'Consultório';
      default: return '';
    }
  };

  const typeLabel = nextAppointment ? getTypeLabel(nextAppointment.type) : '';
  const description = nextAppointment 
    ? `${nextAppointment.doctor?.specialty || 'Consulta'} ${typeLabel ? `(${typeLabel})` : ''} com ${nextAppointment.doctor?.user?.name || 'médico'} ${getStatusDescription(nextAppointment.status)}`
    : undefined;

  return (
    <div 
      onClick={() => navigate('/minhas-consultas')}
      className="cursor-pointer transition-transform hover:scale-[1.01] active:scale-[0.99]"
      title="Clique para ver detalhes"
    >
      <StatCard
        label="Próxima Consulta"
        value={displayValue}
        icon={Calendar}
        color="teal"
        description={description}
      />
    </div>
  );
}
